import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Heart,
  MessageCircle,
  Copy,
  Download,
  Table2,
  FileText,
  Search,
  Clock,
  User,
  Send,
  Trash2,
  MoreHorizontal,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { useTeamStore } from "../../../stores/teamStore";
import { teamService } from "../../../services/teamService";
import { formatDate } from "../../../utils/dateGrouping";
import { copyToClipboard, copyTableAsText, exportToExcel, exportToCSV } from "../../../utils/exportUtils";

// Comment Item Component
const CommentItem = ({ comment, onDelete, isOwner }) => {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div className="flex gap-3 py-3 group">
      <div className="w-8 h-8 rounded-full bg-sedaily-500 flex items-center justify-center flex-shrink-0">
        <User className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium text-text-200">{comment.userName}</span>
          <span className="text-xs text-text-500">{formatDate(comment.createdAt)}</span>
          {isOwner && (
            <div className="relative ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-1 rounded hover:bg-bg-300/50"
              >
                <MoreHorizontal className="w-4 h-4 text-text-400" />
              </button>
              {showMenu && (
                <div
                  className="absolute right-0 top-full mt-1 bg-bg-100 rounded-lg z-10 py-1"
                  style={{ border: '1px solid hsl(var(--border-300) / 0.15)', boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)' }}
                >
                  <button
                    onClick={() => {
                      onDelete(comment.id);
                      setShowMenu(false);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-xs text-red-400 hover:bg-red-500/10 w-full"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <p className="text-sm text-text-300">{comment.content}</p>
      </div>
    </div>
  );
};

// Comment Section Component
const CommentSection = ({ workId, comments: initialComments = [], onCommentChange }) => {
  const [comments, setComments] = useState(initialComments);
  const [newComment, setNewComment] = useState("");
  const { addComment, deleteComment } = useTeamStore();

  const handleSubmit = () => {
    if (!newComment.trim()) return;

    const comment = {
      id: `comment_${Date.now()}`,
      userId: "user_current",
      userName: "Me",
      content: newComment.trim(),
      createdAt: new Date().toISOString(),
    };

    addComment(workId, comment);
    setComments([...comments, comment]);
    setNewComment("");
    toast.success("Comment added");

    if (onCommentChange) {
      onCommentChange(comments.length + 1);
    }
  };

  const handleDelete = (commentId) => {
    deleteComment(workId, commentId);
    setComments(comments.filter((c) => c.id !== commentId));
    toast.success("Comment deleted");

    if (onCommentChange) {
      onCommentChange(comments.length - 1);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="mt-6 pt-6" style={{ borderTop: '1px solid hsl(var(--border-300) / 0.1)' }}>
      <h3 className="text-sm font-semibold text-text-200 mb-4 flex items-center gap-2">
        <MessageCircle className="w-4 h-4" />
        Comments ({comments.length})
      </h3>

      {/* Comment Input */}
      <div className="flex gap-3 mb-4">
        <div className="w-8 h-8 rounded-full bg-sedaily-500 flex items-center justify-center flex-shrink-0">
          <User className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 flex gap-2">
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Write a comment..."
            className="flex-1 bg-bg-200/50 text-text-100 text-sm px-3 py-2 rounded-lg outline-none focus:ring-1 focus:ring-sedaily-500/30 transition-all"
            style={{ border: '1px solid hsl(var(--border-300) / 0.12)' }}
          />
          <button
            onClick={handleSubmit}
            disabled={!newComment.trim()}
            className="px-3 py-2 bg-sedaily-500 text-white text-sm rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-sedaily-600 transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Comments List */}
      <div className="space-y-0">
        {comments.length === 0 ? (
          <p className="text-sm text-text-400 text-center py-4">
            No comments yet. Be the first to comment!
          </p>
        ) : (
          comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              onDelete={handleDelete}
              isOwner={comment.userId === "user_current"}
            />
          ))
        )}
      </div>
    </div>
  );
};

// Main Work Detail Component
const WorkDetail = () => {
  const { workId } = useParams();
  const navigate = useNavigate();
  const { works, toggleLike } = useTeamStore();
  const [work, setWork] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [copied, setCopied] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);

  // Load work data
  useEffect(() => {
    const loadWork = async () => {
      setIsLoading(true);
      try {
        // First check store
        let foundWork = works.find((w) => w.id === workId);

        if (!foundWork) {
          // Load from service
          foundWork = await teamService.getWorkById(workId);
        }

        if (foundWork) {
          setWork(foundWork);
          setIsLiked(foundWork.likedBy?.includes("user_current") || false);
          setLikeCount(foundWork.likes || 0);
        } else {
          navigate("/team");
        }
      } catch (error) {
        console.error("Failed to load work:", error);
        navigate("/team");
      } finally {
        setIsLoading(false);
      }
    };

    loadWork();
  }, [workId, works, navigate]);

  const handleLike = () => {
    const newLiked = !isLiked;
    setIsLiked(newLiked);
    setLikeCount((prev) => (newLiked ? prev + 1 : prev - 1));
    toggleLike(workId, "user_current");
    toast.success(newLiked ? "Liked!" : "Like removed");
  };

  const handleCopy = async () => {
    if (work.table) {
      await copyTableAsText(work.table);
    } else {
      await copyToClipboard(work.content);
    }
    setCopied(true);
    toast.success("Copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = (format) => {
    if (work.table) {
      if (format === "excel") {
        exportToExcel(work.table, work.title || "export");
        toast.success("Excel file downloaded");
      } else if (format === "csv") {
        exportToCSV(work.table, work.title || "export");
        toast.success("CSV file downloaded");
      }
    }
    setDownloadOpen(false);
  };

  const getWorkTypeIcon = () => {
    switch (work?.workType) {
      case "table":
        return <Table2 className="w-4 h-4" />;
      case "document":
        return <FileText className="w-4 h-4" />;
      case "search":
        return <Search className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getWorkTypeLabel = () => {
    switch (work?.workType) {
      case "table":
        return "Table";
      case "document":
        return "Document";
      case "search":
        return "Search";
      default:
        return "Other";
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-sedaily-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!work) {
    return null;
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate("/team")}
          className="flex items-center gap-2 text-text-400 hover:text-text-100 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Team Library
        </button>

        {/* Work Header */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-bg-200/60 rounded-2xl overflow-hidden"
          style={{ border: '1px solid hsl(var(--border-300) / 0.12)' }}
        >
          {/* Header Section */}
          <div className="p-6" style={{ borderBottom: '1px solid hsl(var(--border-300) / 0.1)' }}>
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                {/* Type Badge */}
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-sedaily-500/10 text-sedaily-400 text-xs rounded-md mb-3">
                  {getWorkTypeIcon()}
                  {getWorkTypeLabel()}
                </span>

                {/* Title */}
                <h1 className="text-2xl font-bold text-text-100 mb-2">{work.title}</h1>

                {/* Description */}
                {work.description && (
                  <p className="text-text-400 text-sm">{work.description}</p>
                )}

                {/* Tags */}
                {work.tags && work.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {work.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs text-text-400 bg-bg-300/50 px-2 py-0.5 rounded-full"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={handleLike}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    isLiked
                      ? "bg-red-500/10 text-red-400"
                      : "bg-bg-300/50 text-text-400 hover:bg-bg-300 hover:text-text-100"
                  }`}
                >
                  <Heart className={`w-4 h-4 ${isLiked ? "fill-current" : ""}`} />
                  {likeCount}
                </button>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1.5 px-3 py-2 bg-bg-300/50 text-text-400 hover:bg-bg-300 hover:text-text-100 rounded-lg text-sm font-medium transition-all"
                >
                  <Copy className="w-4 h-4" />
                  {copied ? "Copied" : "Copy"}
                </button>
                {work.table && (
                  <div className="relative">
                    <button
                      onClick={() => setDownloadOpen(!downloadOpen)}
                      className="flex items-center gap-1.5 px-3 py-2 bg-bg-300/50 text-text-400 hover:bg-bg-300 hover:text-text-100 rounded-lg text-sm font-medium transition-all"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                    {downloadOpen && (
                      <div
                        className="absolute right-0 top-full mt-1 bg-bg-100 rounded-lg z-10 py-1 min-w-[120px]"
                        style={{ border: '1px solid hsl(var(--border-300) / 0.15)', boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)' }}
                      >
                        <button
                          onClick={() => handleDownload("excel")}
                          className="w-full px-3 py-2 text-xs text-left text-text-300 hover:bg-bg-200 hover:text-text-100"
                        >
                          Excel (.xlsx)
                        </button>
                        <button
                          onClick={() => handleDownload("csv")}
                          className="w-full px-3 py-2 text-xs text-left text-text-300 hover:bg-bg-200 hover:text-text-100"
                        >
                          CSV (.csv)
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Author Info */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-sedaily-500 flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-text-200">{work.userName}</p>
                <p className="text-xs text-text-400 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatDate(work.createdAt)}
                  {work.department && (
                    <>
                      <span className="mx-1">·</span>
                      {work.department}
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-6">
            {work.table ? (
              <div className="chatbot-table-wrapper">
                <table>
                  <thead>
                    <tr>
                      {work.table.headers?.map((header, i) => (
                        <th key={i}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {work.table.rows?.map((row, i) => (
                      <tr key={i}>
                        {row.map((cell, j) => (
                          <td key={j}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-text-200 text-sm whitespace-pre-wrap prose prose-sm max-w-none">
                {work.content}
              </div>
            )}

            {/* Comments Section */}
            <CommentSection
              workId={work.id}
              comments={work.comments || []}
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default WorkDetail;
